import React, { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { mobileMenuNavItems, mobileMenuAdminNavItems } from '../../constants/navigation';

const linkClassName = ({ isActive }) =>
  `flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 ${
    isActive
      ? 'bg-[var(--text-main)]/5 text-[var(--text-main)] font-extrabold shadow-sm'
      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-base)] hover:text-[var(--text-main)]'
  }`;

export default function MobileNavMenu({ isOpen, onClose }) {
  const { isDark, toggleTheme } = useTheme();
  const { can } = useAuth();
  const { pathname } = useLocation();

  const prevPathname = useRef(pathname);

  useEffect(() => {
    if (prevPathname.current !== pathname) {
      prevPathname.current = pathname;
      onClose();
    }
  }, [pathname, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const visibleAdminItems = mobileMenuAdminNavItems.filter((item) => can(item.permission));

  return (
    <div className="md:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Menú de navegación">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Cerrar menú"
      />

      <div className="absolute bottom-0 left-0 right-0 bg-[var(--bg-surface)] border-t border-[var(--border-subtle)] rounded-t-2xl shadow-2xl pb-safe animate-slide-up">
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-[var(--border-subtle)]" aria-hidden="true" />
        </div>

        <div className="px-4 pb-4 max-h-[70vh] overflow-y-auto">
          <p className="px-4 pb-2 text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">
            Más opciones
          </p>

          <nav className="space-y-1">
            {mobileMenuNavItems.map((item) =>
              item.disabled ? (
                <div
                  key={item.label}
                  className="flex items-center gap-4 px-4 py-3.5 text-[var(--text-secondary)] opacity-40 cursor-not-allowed"
                >
                  <SafeIcon name={item.icon} className="w-5 h-5" />
                  <span className="font-semibold text-sm">{item.label}</span>
                </div>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/app'}
                  className={linkClassName}
                  onClick={onClose}
                >
                  <SafeIcon name={item.icon} className="w-5 h-5" />
                  <span className="font-semibold text-sm">{item.label}</span>
                </NavLink>
              )
            )}

            {visibleAdminItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={linkClassName}
                onClick={onClose}
              >
                <SafeIcon name={item.icon} className="w-5 h-5" />
                <span className="font-semibold text-sm">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="mt-3 pt-3 border-t border-[var(--border-subtle)]">
            <button
              type="button"
              onClick={toggleTheme}
              className="w-full flex items-center gap-4 px-4 py-3.5 text-[var(--text-secondary)] hover:bg-[var(--bg-base)] hover:text-[var(--text-main)] rounded-xl transition-all"
            >
              <SafeIcon name={isDark ? 'Sun' : 'Moon'} className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-widest">
                {isDark ? 'Modo Día' : 'Modo Noche'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
